import { useState, useCallback, useRef, useEffect } from 'react';

/**
 * CUSTOM HOOK: useApiCache
 *
 * Provides intelligent API response caching:
 * - In-memory cache with TTL (time to live)
 * - Automatic cache invalidation
 * - Cache warming (pre-fetch)
 * - Stale-while-revalidate strategy
 * - Memory-efficient with automatic cleanup
 *
 * Fixes: Repeated API calls, slow page navigation, unnecessary network requests
 */

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  expiresAt: number;
  isStale: boolean;
}

interface CacheOptions {
  ttl?: number; // Time to live in milliseconds
  staleTime?: number; // Time before data is considered stale
  maxSize?: number; // Maximum number of entries
}

const DEFAULT_OPTIONS: Required<CacheOptions> = {
  ttl: 5 * 60 * 1000, // 5 minutes
  staleTime: 2 * 60 * 1000, // 2 minutes (data is fresh)
  maxSize: 50, // Maximum 50 cached entries
};

export const useApiCache = <T = any>(options?: CacheOptions) => {
  const config = { ...DEFAULT_OPTIONS, ...options };
  const cacheRef = useRef<Map<string, CacheEntry<T>>>(new Map());
  const [cacheSize, setCacheSize] = useState(0);

  // Clean up expired entries
  const cleanupExpired = useCallback(() => {
    const now = Date.now();
    const toDelete: string[] = [];

    cacheRef.current.forEach((entry, key) => {
      if (now > entry.expiresAt) {
        toDelete.push(key);
      }
    });

    toDelete.forEach(key => {
      cacheRef.current.delete(key);
    });

    if (toDelete.length > 0) {
      console.log(`[API Cache] Cleaned up ${toDelete.length} expired entries`);
      setCacheSize(cacheRef.current.size);
    }
  }, []);

  // Enforce max cache size by removing oldest entries
  const enforceMaxSize = useCallback(() => {
    if (cacheRef.current.size <= config.maxSize) return;

    // Sort by timestamp and remove oldest
    const entries = Array.from(cacheRef.current.entries())
      .sort((a, b) => a[1].timestamp - b[1].timestamp);

    const toRemove = entries.slice(0, entries.length - config.maxSize);
    toRemove.forEach(([key]) => {
      cacheRef.current.delete(key);
    });

    console.log(`[API Cache] Removed ${toRemove.length} entries to enforce max size`);
    setCacheSize(cacheRef.current.size);
  }, [config.maxSize]);

  // Get data from cache
  const get = useCallback((key: string): T | null => {
    const entry = cacheRef.current.get(key);

    if (!entry) {
      return null;
    }

    const now = Date.now();

    // Check if expired
    if (now > entry.expiresAt) {
      console.log(`[API Cache] Entry expired for key: ${key}`);
      cacheRef.current.delete(key);
      setCacheSize(cacheRef.current.size);
      return null;
    }

    // Mark as stale if past stale time
    if (now > entry.timestamp + config.staleTime) {
      entry.isStale = true;
    }

    console.log(`[API Cache] Cache ${entry.isStale ? 'HIT (stale)' : 'HIT'} for key: ${key}`);
    return entry.data;
  }, [config.staleTime]);

  // Check if data is stale
  const isStale = useCallback((key: string): boolean => {
    const entry = cacheRef.current.get(key);
    return entry?.isStale ?? true;
  }, []);

  // Set data in cache
  const set = useCallback((key: string, data: T): void => {
    const now = Date.now();

    const entry: CacheEntry<T> = {
      data,
      timestamp: now,
      expiresAt: now + config.ttl,
      isStale: false,
    };

    cacheRef.current.set(key, entry);
    setCacheSize(cacheRef.current.size);

    console.log(`[API Cache] Cached data for key: ${key} (TTL: ${config.ttl}ms)`);

    // Enforce max size
    enforceMaxSize();
  }, [config.ttl, enforceMaxSize]);

  // Invalidate specific key or all keys
  const invalidate = useCallback((key?: string): void => {
    if (key) {
      cacheRef.current.delete(key);
      console.log(`[API Cache] Invalidated cache for key: ${key}`);
    } else {
      cacheRef.current.clear();
      console.log('[API Cache] Invalidated all cache');
    }
    setCacheSize(cacheRef.current.size);
  }, []);

  // Cached fetch wrapper
  const cachedFetch = useCallback(async <TData = T,>(
    key: string,
    fetchFn: () => Promise<TData>,
    options?: { forceRefresh?: boolean; backgroundRefresh?: boolean }
  ): Promise<TData> => {
    const forceRefresh = options?.forceRefresh ?? false;
    const backgroundRefresh = options?.backgroundRefresh ?? true;

    // Check cache first (unless force refresh)
    if (!forceRefresh) {
      const cachedData = get(key) as TData | null;

      if (cachedData !== null) {
        // If data is stale and background refresh is enabled, fetch in background
        if (isStale(key) && backgroundRefresh) {
          console.log(`[API Cache] Returning stale data and refreshing in background for key: ${key}`);

          // Fetch in background without awaiting
          fetchFn()
            .then(freshData => {
              set(key, freshData as any);
              console.log(`[API Cache] Background refresh completed for key: ${key}`);
            })
            .catch(error => {
              console.error(`[API Cache] Background refresh failed for key: ${key}:`, error);
            });
        }

        return cachedData;
      }
    }

    // Cache miss or force refresh - fetch data
    console.log(`[API Cache] Cache MISS for key: ${key} - fetching...`);

    try {
      const data = await fetchFn();
      set(key, data as any);
      return data;
    } catch (error) {
      // On error, try to return stale data if available
      const staleData = get(key) as TData | null;
      if (staleData !== null) {
        console.warn(`[API Cache] Fetch failed, returning stale data for key: ${key}`);
        return staleData;
      }

      throw error;
    }
  }, [get, set, isStale]);

  // Pre-fetch data (cache warming)
  const prefetch = useCallback(async (key: string, fetchFn: () => Promise<T>): Promise<void> => {
    console.log(`[API Cache] Prefetching data for key: ${key}`);

    try {
      const data = await fetchFn();
      set(key, data);
    } catch (error) {
      console.error(`[API Cache] Prefetch failed for key: ${key}:`, error);
    }
  }, [set]);

  // Get cache statistics
  const getStats = useCallback(() => {
    return {
      size: cacheRef.current.size,
      maxSize: config.maxSize,
      utilizationPercent: (cacheRef.current.size / config.maxSize) * 100,
      entries: Array.from(cacheRef.current.entries()).map(([key, entry]) => ({
        key,
        age: Date.now() - entry.timestamp,
        isStale: entry.isStale,
        expiresIn: entry.expiresAt - Date.now(),
      })),
    };
  }, [config.maxSize]);

  // Periodic cleanup
  useEffect(() => {
    console.log('[API Cache] Initializing cache manager');

    const cleanupInterval = setInterval(() => {
      cleanupExpired();
    }, 60000); // Cleanup every minute

    return () => {
      console.log('[API Cache] Cleaning up cache manager');
      clearInterval(cleanupInterval);
      cacheRef.current.clear();
    };
  }, [cleanupExpired]);

  return {
    get,
    set,
    invalidate,
    cachedFetch,
    prefetch,
    isStale,
    getStats,
    cacheSize,
  };
};
